import { v4 as uuidv4 } from 'uuid';
import { createSelector } from 'reselect';
import i18next from 'i18next';

import { TYPE_LOGOUT, TYPE_LOGOUT_SOFT } from 'constants/Login';
import { getHasUndismissedErrorsOfType } from 'reducers/Error';
import { RootState } from 'reducers';

import * as mp from '../constants/modalPage';

type ModalPageState = {
  id: string;
  component: string;
  props: any;
  modalClassName: string;
  isPopup: boolean;
  groupID?: string;
  replace?: boolean;
};

const initState = {
  components: [] as ModalPageState[],
};

// TODO: Refactor actions and reducer so that all the props are passed via payload

const modalPage = (
  state = initState,
  {
    id,
    type,
    component,
    props = {},
    modalClassName,
    isPopup,
    groupID,
    replace,
  }: ModalPageState & { type: string },
) => {
  switch (type) {
    case TYPE_LOGOUT:
    case TYPE_LOGOUT_SOFT:
      return initState;
    case mp.OPEN_MODAL_PAGE:
      // eslint-disable-next-line no-case-declarations
      const newMP = {
        id: id || uuidv4(),
        component,
        props,
        modalClassName,
        isPopup,
        groupID,
      };
      // replace && groupID replaces all cp from the group with the payload component
      // replace only replaces the last component
      if (replace) {
        return {
          ...state,
          components: [
            ...(groupID
              ? state.components.filter(
                  c => !c.groupID || c.groupID !== groupID,
                )
              : state.components.slice(0, -1)),
            newMP,
          ],
        };
      }
      return {
        ...state,
        components: [...state.components, newMP],
      };
    case mp.PREVIOUS_MODAL_PAGE: {
      const lastPopup = state.components
        .filter(comp => comp.isPopup)
        .slice(-1)[0];
      const last = state.components.slice(-1)[0];
      const closeID = (lastPopup || last || {}).id;
      return {
        ...state,
        components: state.components.filter(({ id }) => id !== closeID),
      };
    }
    case mp.CLOSE_MODAL_PAGE:
      if (id) {
        return {
          ...state,
          components: state.components.filter(({ id: pid }) => pid !== id),
        };
      }
      if (groupID) {
        return {
          ...state,
          components: state.components.filter(c => c.groupID !== groupID),
        };
      }
      return initState;
    default:
      return state;
  }
};

export default modalPage;

export const getComponents = (state: RootState) => state.modalPage.components;

export const getModalPages = createSelector(getComponents, components =>
  components.filter(comp => !comp.isPopup),
);
export const getModalPopups = createSelector(getComponents, components =>
  components.filter(comp => comp.isPopup),
);

// region Blocking scanning
const getScannerNonWhitelistComponent = createSelector(
  getComponents,
  components =>
    components
      .map(comp => comp.component)
      .find(comp => !mp.scannerComponentWhiteList.includes(comp)),
);
const getScannerBlacklistedAlert = (state: RootState) =>
  mp.scannerErrorTypesBlackList.find(et =>
    getHasUndismissedErrorsOfType(et)(state),
  );

type MessageType = 'componentBlacklist' | 'alertBlacklist';
export const getMessageIfScanningShouldBeBlocked = createSelector(
  getScannerNonWhitelistComponent,
  getScannerBlacklistedAlert,
  (component, alert): undefined | [MessageType, string] => {
    if (component) {
      const translation = i18next.t('modalpage:modalpageName', {
        context: component,
      });
      return ['componentBlacklist', translation];
    }
    if (alert) return ['alertBlacklist', alert];
    return undefined;
  },
);
// endregion
// region Blocking shopping cart
const getShoppingcartBlacklistedComponent = createSelector(
  getComponents,
  components =>
    components
      .map(comp => comp.component)
      .find(comp => mp.shoppingcartComponentsBlacklist.includes(comp)),
);
const getShoppingcartBlacklistedAlert = (state: RootState) =>
  mp.shoppingcartErrorTypesBlacklist.find(et =>
    getHasUndismissedErrorsOfType(et)(state),
  );

export const getMessageIfShoppingcartShouldBeBlocked = createSelector(
  getShoppingcartBlacklistedComponent,
  getShoppingcartBlacklistedAlert,
  (component, alert) => {
    if (component) return ['componentBlacklist', component];
    if (alert) return ['alertBlacklist', alert];
    return undefined;
  },
);
// endregion

export const getCurrentModalPage = (state: RootState) =>
  getModalPages(state)[getModalPages(state).length - 1] || null;

export const getCurrentComponent = (state: RootState) =>
  getComponents(state)[getComponents(state).length - 1] || null;

export const getCurrentModalPopup = (state: RootState) =>
  getModalPopups(state)[getModalPopups(state).length - 1] || null;

export const hasOpenModalPage = (state: RootState) =>
  getCurrentModalPage(state) !== null;
export const hasOpenModalPopup = (state: RootState) =>
  getCurrentModalPopup(state) !== null;

export const getIsModalOpened = (state: RootState) =>
  getComponents(state).length > 0;
